import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import type { CountryDto } from '@/ApiClient';

// Lets "turkiye" find "Türkiye" for users without the accented letter on their keyboard.
export function foldForSearch(text: string) {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

export const useCountries = defineStore('countries', () => {
  const api = useApi();
  const loaded = ref([]) as Ref<CountryDto[]>;

  async function loadAll() {
    loaded.value = await api.country_List();

    return loaded.value;
  }

  const all = computed(() => loaded.value.map((country) => country.name));

  function codeAlpha3FromName(name: string) {
    return (getByName(name) || {}).codeAlpha3;
  }

  function getByName(name?: string) {
    if (!name) {
      return undefined;
    }
    return loaded.value.find((country) => country.name.toLowerCase() === name.toLowerCase());
  }

  function getFlagPathFromName(name?: string) {
    if (!name) {
      return;
    }
    const codeAlpha3 = codeAlpha3FromName(name);

    if (codeAlpha3 === undefined) {
      console.warn(`Failed to get flag icon for country ${name}`);
      return;
    }

    return `/plm-static/flags/${codeAlpha3.toLowerCase()}.svg`;
  }

  function getById(id: number) {
    return loaded.value.find((country) => country.id === id);
  }

  function matches(country: CountryDto, text: string) {
    const searchText = foldForSearch(text);

    return [country.name, ...(country.searchVariants ?? [])].some((candidate) =>
      foldForSearch(candidate).includes(searchText)
    );
  }

  function search(text: string, countries: CountryDto[] = loaded.value): CountryDto[] {
    return countries.filter((country) => matches(country, text));
  }

  return {
    loadAll,
    loaded,
    all,
    getById,
    codeAlpha3FromName,
    getByName,
    getFlagPathFromName,
    matches,
    search,
  };
});
